/**
 * PageLoader — the one thing drawn when the screen knows nothing yet.
 *
 * CLAUDE.md, "Loading states": this is the *narrow* case. A list, a table, a tree or a detail
 * pane whose shape is known before its rows arrive draws that shape in `Skeleton` bars; the
 * section belongs only to the moments where no layout can be promised at all — the session
 * guard, a workspace or project shell before its first answer. Draw it past `useLongWait`'s
 * delay, never on its own: a fast answer draws nothing.
 *
 * Geometry and timing are `design/project/loader/index.html`'s, owner-approved 2026-09-12 and
 * carried over coordinate for coordinate. Colours and every `animation:` moved into the
 * stylesheet — the module header there says why the second half is not a preference.
 *
 * **The five storeys are a loop, not forty-five hand-written elements.** Each is the previous
 * one twelve units up, with the stair flight alternating direction, which is exactly what the
 * design file spells out five times. The timing is what differs per storey, so the class that
 * carries the animation is looked up per index and the geometry is computed.
 *
 * **The pattern ids are per-instance**, for `FloorPlanPattern`'s reason: two loaders mounted
 * at once would be two elements sharing one `id`, and every `url(#…)` in the document then
 * resolves to whichever copy the parser saw first.
 *
 * `aria-hidden` on the drawing, `role="status"` on the wrapper: the sentence is the accessible
 * status and the section is the wallpaper it sits on.
 */

import type { CSSProperties } from 'react';
import { useId } from 'react';

import styles from './PageLoader.module.css';

/** Ground level, then one per slab. Index is the level, not the storey. */
const LEVELS = [styles.lvl0, styles.lvl1, styles.lvl2, styles.lvl3, styles.lvl4, styles.lvl5];

/** One entry per storey; the order is the order the storey assembles in. */
const STOREYS = [
  { wall: styles.wall0, core: styles.core0, stair: styles.stair0, win: styles.win0, slab: styles.slab0 },
  { wall: styles.wall1, core: styles.core1, stair: styles.stair1, win: styles.win1, slab: styles.slab1 },
  { wall: styles.wall2, core: styles.core2, stair: styles.stair2, win: styles.win2, slab: styles.slab2 },
  { wall: styles.wall3, core: styles.core3, stair: styles.stair3, win: styles.win3, slab: styles.slab3 },
  { wall: styles.wall4, core: styles.core4, stair: styles.stair4, win: styles.win4, slab: styles.slab4 },
];

/** Ground line, and the floor-to-floor the whole section is built on. */
const GROUND_Y = 88;
const STOREY_HEIGHT = 12;

function cx(...parts: readonly (string | undefined | false)[]): string {
  return parts.filter(Boolean).join(' ');
}

/** The level tag at the far left: a leader line and the filled triangle that points at it. */
function levelMark(y: number): string {
  return `M9 ${String(y)} H21 M15 ${String(y)} l-1.6 -2.6 h3.2 z`;
}

export interface PageLoaderProps {
  /**
   * The sentence beneath the drawing — the one the screen already uses, translated by the
   * caller (`t('status.loading')`, `t('session.checking')`). A loader with no sentence is a
   * spinner, which is the thing this replaces.
   */
  readonly label: string;
  /**
   * Where in the 7s loop to start, as milliseconds on the `performance` clock.
   *
   * It is only ever meaningful for the one loader that replaces the boot loader in
   * `index.html`: that drawing started when its stylesheet applied, which is ~0 on the same
   * clock, so `-performance.now()` puts this copy at the frame it had reached and the hand-over
   * is a change of renderer rather than a restart. Every other loader leaves it undefined and
   * begins at 0. `features/session/RequireSession` is the only caller — see `lib/boot.ts`.
   */
  readonly phaseMs?: number | undefined;
}

export function PageLoader({ label, phaseMs }: PageLoaderProps) {
  const instance = useId().replace(/:/g, '');
  const gridId = `kalqa-loader-grid-${instance}`;
  const hatchId = `kalqa-loader-hatch-${instance}`;

  return (
    <div
      className={styles.loader}
      role="status"
      // A custom property through CSSOM, not a `style=""` attribute in the HTML — the CSP is
      // `style-src 'self'`, which is also why the boot loader's CSS is a file.
      style={phaseMs === undefined ? undefined : ({ '--kalqa-loader-phase': `-${String(phaseMs)}ms` } as CSSProperties)}
    >
      <svg className={styles.plan} width="200" height="200" viewBox="0 0 100 100" aria-hidden>
        <defs>
          <pattern id={gridId} width="5" height="5" patternUnits="userSpaceOnUse">
            <path className={styles.gridLine} d="M5 0H0V5" />
          </pattern>
          <pattern
            id={hatchId}
            width="2"
            height="2"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <path className={styles.hatchLine} d="M0 0 V2" />
          </pattern>
        </defs>
        <rect className={styles.paper} width="100" height="100" />
        <rect width="100" height="100" fill={`url(#${gridId})`} />
        <g className={styles.sheet}>
          <path className={cx(styles.ink, styles.ground)} d={`M14 ${String(GROUND_Y)} H86`} />
          <rect
            className={styles.foundation}
            fill={`url(#${hatchId})`}
            x="22"
            y={GROUND_Y}
            width="56"
            height="4"
          />
          <path className={cx(styles.ink, styles.footing)} d={`M22 ${String(GROUND_Y + 4)} H78`} />
          <g className={LEVELS[0]}>
            <path className={cx(styles.ink, styles.levelMark)} d={levelMark(GROUND_Y)} />
          </g>
          {STOREYS.map((storey, index) => {
            const base = GROUND_Y - index * STOREY_HEIGHT;
            const top = base - STOREY_HEIGHT;
            const windowY = base - 9;
            // The flight reverses every storey, so a person's eye walks up the core rather
            // than restarting at the bottom of it five times.
            const stair =
              index % 2 === 0
                ? `M60 ${String(base)} h2 v-3 h2 v-3 h2 v-3 h2 v-3`
                : `M68 ${String(base)} h-2 v-3 h-2 v-3 h-2 v-3 h-2 v-3`;

            return (
              <g key={base}>
                <path className={cx(styles.ink, styles.wall, storey.wall)} d={`M28 ${String(base)} V${String(top)}`} />
                <path className={cx(styles.ink, styles.wall, storey.wall)} d={`M72 ${String(base)} V${String(top)}`} />
                <path
                  className={cx(styles.ink, styles.core, storey.core)}
                  d={`M59 ${String(base)} V${String(top)} M69 ${String(base)} V${String(top)}`}
                />
                <path className={cx(styles.ink, styles.stair, storey.stair)} d={stair} />
                <rect
                  className={cx(styles.ink, styles.window, storey.win)}
                  x="33"
                  y={windowY}
                  width="9"
                  height="6"
                />
                <path
                  className={cx(styles.ink, styles.mullion, storey.win)}
                  d={`M37.5 ${String(windowY)} v6`}
                />
                <rect
                  className={cx(styles.ink, styles.window, storey.win)}
                  x="45"
                  y={windowY}
                  width="9"
                  height="6"
                />
                <path
                  className={cx(styles.ink, styles.mullion, storey.win)}
                  d={`M49.5 ${String(windowY)} v6`}
                />
                <path className={cx(styles.ink, styles.slab, storey.slab)} d={`M24 ${String(top)} H76`} />
                <g className={LEVELS[index + 1]}>
                  <path className={cx(styles.ink, styles.levelMark)} d={levelMark(top)} />
                </g>
              </g>
            );
          })}
          <path className={cx(styles.ink, styles.parapet)} d="M28 28 V24 M72 28 V24" />
          <path className={cx(styles.ink, styles.cap)} d="M26.5 24 H29.5 M70.5 24 H73.5" />
          <g className={styles.sectionMark}>
            <path className={cx(styles.ink, styles.levelMark)} d="M82 20 V96 M82 20 H85 M82 96 H85" />
            <text className={styles.fig} x="86.5" y="22.4">{'A'}</text>
            <text className={styles.fig} x="86.5" y="98.4">{'A'}</text>
          </g>
        </g>
      </svg>
      <p className={styles.label}>{label}</p>
    </div>
  );
}
